import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { SPRING } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';

interface OnboardingStepperProps {
  steps: { title: string; description: string }[];
  currentIndex: number;
  /** Última etapa que pode ser aberta pelo stepper (as seguintes estão bloqueadas por dado obrigatório). */
  lastReachable: number;
  onSelect: (index: number) => void;
}

// Stepper vertical do onboarding (desktop): uma linha por etapa, com descrição e conector.
export function OnboardingStepper({ steps, currentIndex, lastReachable, onSelect }: OnboardingStepperProps) {
  const progress = Math.round((currentIndex / (steps.length - 1)) * 100);

  return (
    <nav aria-label="Etapas da configuração">
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs font-medium text-ink-secondary">
          <span>Progresso</span>
          <span>{progress}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-chip">
          <motion.div
            className="h-full rounded-full bg-primary"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <ol className="flex flex-col">
        {steps.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isReachable = index <= lastReachable;
          const isLast = index === steps.length - 1;

          return (
            <li key={step.title} className="relative flex gap-3">
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={cn('absolute left-[13px] top-8 h-[calc(100%-24px)] w-px', isDone ? 'bg-primary' : 'bg-border')}
                />
              )}
              <button
                type="button"
                onClick={() => onSelect(index)}
                disabled={!isReachable}
                aria-current={isCurrent ? 'step' : undefined}
                className="group relative flex w-full items-start gap-3 rounded-xl py-2 pr-2 text-left disabled:cursor-not-allowed"
              >
                <span
                  className={cn(
                    'relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                    isDone && 'border-primary bg-primary text-surface',
                    isCurrent && 'border-primary bg-surface text-primary ring-4 ring-primary/15',
                    !isDone && !isCurrent && 'border-border bg-surface text-ink-secondary'
                  )}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" /> : index + 1}
                </span>
                <span className="pb-4 pt-0.5">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      isCurrent ? 'text-ink' : isReachable ? 'text-ink-secondary group-hover:text-ink' : 'text-ink-secondary/50'
                    )}
                  >
                    {step.title}
                  </span>
                  {isCurrent && (
                    <motion.span
                      layoutId="onboarding-step-description"
                      transition={SPRING}
                      className="mt-0.5 block text-xs text-ink-secondary"
                    >
                      {step.description}
                    </motion.span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
